import { usePage } from "@inertiajs/react"
import { LoaderCircle, UserX } from "lucide-react"
import { UserLayout } from "@/components/layouts/user-layout"
import { entryState, type PendingEntryRequest } from "@/features/user/entry-state"
import { useEntryRequestWatch } from "@/features/user/use-entry-request-watch"
import { t } from "@/lib/i18n"

type EntryPayload = {
  token: string
  locationName: string | null
  tableName: string | null
  maxNameLength: number
  pendingRequest: PendingEntryRequest | null
  error: string | null
}

type Props = {
  entry: EntryPayload
}

/**
 * The door behind a static invite, once a diner has asked: `User::InvitesController`
 * only ever renders this for a pending request or one a person refused. Two
 * states, not four — the geofence, the ask form and the "expired" screen are
 * gone (#155 phase 4). The ask itself now lives on the pre-seating menu
 * (`user/invites/menu`), where a lapsed request is redirected back to instead
 * of shown here: nothing is being overridden, so there is nothing this screen
 * needs to say about it.
 */
export default function UserInvitesEntry() {
  const { entry } = usePage<Props>().props
  const { waiting } = entryState({ request: entry.pendingRequest })

  // While it is pending, the answer arrives in someone else's browser. Without
  // this the diner sits on "waiting for approval" after they have already been
  // let in, and calls the waiter over — the exact intervention the feature was
  // built to remove.
  useEntryRequestWatch({
    enabled: waiting,
    path: `/invites/${entry.token}`,
    expiresInSeconds: entry.pendingRequest?.expiresInSeconds ?? null,
  })

  return (
    <UserLayout locationName={entry.locationName ?? undefined}>
      <div
        data-testid="invite-entry-view"
        className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 py-16 text-center"
      >
        {waiting ? (
          <div data-testid="invite-entry-waiting" className="space-y-2">
            <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <LoaderCircle className="size-7 animate-spin" />
            </span>
            <h1 className="text-xl font-semibold tracking-tight">
              {t("ui.react.pages.user.invites.waiting.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("ui.react.pages.user.invites.waiting.body")}
            </p>
          </div>
        ) : (
          <div data-testid="invite-entry-refused" className="space-y-2">
            <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-warning/12 text-warning">
              <UserX className="size-7" />
            </span>
            <h1 className="text-xl font-semibold tracking-tight">
              {t("ui.react.pages.user.invites.refused.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {entry.error || t("ui.react.pages.user.invites.refused.body")}
            </p>
          </div>
        )}
      </div>
    </UserLayout>
  )
}
